import { FC, useContext } from 'react';

import {
  DoubleCheckIcon,
  Label,
  Paragraph,
  Separator,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { FeatureHOC } from 'features';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { sanitizeHTML } from 'utils';

import { Rate } from '../Rate';
import { fixTextFormatters, formatDate } from '../utils';
import { IRateProps } from './interfaces';
import s from './Review.module.scss';

export const Review: FC<IRateProps> = ({
  data: {
    id,
    is_read,
    host_group_name,
    host_alias,
    author,
    created_at,
    grade,
    text,
    request_contact,
    request_contact_info,
    tips,
    worker_name,
  },
  openClientModal,
  readClubReview,
  onClick,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const {
    state: {
      settings: {
        currency: { sign },
      },
    },
  } = useContext(SettingsContext);

  return (
    <div
      className={cn(s.container, is_read && s.read)}
      onClick={() => onClick(id)}
      onMouseEnter={() => {
        if (!text && !is_read) {
          readClubReview(id);
        }
      }}
    >
      <div className={s.iconContainer}>
        {is_read ? <DoubleCheckIcon size="m" /> : <div className={s.dot} />}
      </div>
      <div className={s.block}>
        <span
          className={cn(author && permissions?.has('client_info') && s.author)}
          onClick={(e) => {
            e.stopPropagation();

            if (!permissions?.has('client_info')) return;
            author && openClientModal(author.uuid);
          }}
        >
          {author?.nickname ?? t('client.guest')}
        </span>
        <div className={s.additional}>
          <Rate rate={grade} />
          <div className={s.hostData}>
            <span>{host_alias}</span>
            <span>{host_group_name}</span>
          </div>
        </div>
      </div>
      <div className={s.block}>
        <div
          className={s.date}
          dangerouslySetInnerHTML={{
            __html: formatDate(sanitizeHTML(created_at)),
          }}
        />
        {text && <div className={s.text}>{fixTextFormatters(text)}</div>}
        {request_contact_info && (
          <div className={s.contact_block}>
            <Separator />
            <div className={s.contact}>
              <span className={s.contact_title}>
                {t('review.contact.info')}
              </span>
              <span className={s.contact_info}>{request_contact_info}</span>
            </div>
          </div>
        )}
      </div>
      {(request_contact || Boolean(tips)) && (
        <div className={s.footer}>
          {request_contact && (
            <Label
              iconName="SpeakerIcon"
              size="s"
              text={t('review.contact.tried')}
            />
          )}
          <FeatureHOC alias="tips">
            {Boolean(tips) && (
              <div className={s.tips}>
                <Paragraph size="s" color="secondary">
                  {worker_name}
                </Paragraph>
                <Label
                  iconName="HandCoinIcon"
                  size="s"
                  color="green"
                  text={`${tips || 0} ${sign}`}
                />
              </div>
            )}
          </FeatureHOC>
        </div>
      )}
    </div>
  );
};
